import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const cervicalStenosis: ContentBlock<"condition"> = {
  id: makeId("dx-cervical-stenosis"),
  kind: "condition",
  tags: ["her", "evidence-based"],
  title: "Cervical stenosis",
  subtitle: "Narrowed cervical canal that can complicate fertility procedures",
  body: {
    conditionName: "Cervical stenosis",
    category: "structural",
    description:
      "Cervical stenosis is a narrowing of the cervical canal — the passage between the vagina and the uterus. It can be congenital (present from birth) or acquired after procedures like LEEP, cone biopsy, cervical cauterization, or radiation. While it does not directly prevent conception in all cases, it can make intrauterine procedures (IUI, embryo transfer, HSG) technically difficult and may impair sperm transport.",
    symptoms: [
      "Difficult or painful embryo transfer attempts",
      "Difficulty passing a catheter during IUI or HSG",
      "Light or absent periods (if the canal is severely narrowed)",
      "Painful periods with cramping (menstrual blood unable to flow freely)",
      "Often no symptoms — discovered during a procedure",
    ],
    howDiagnosed:
      "Usually discovered during a procedure when the catheter cannot easily pass through the cervical canal. A mock embryo transfer or trial transfer is specifically designed to identify this issue before an actual embryo transfer cycle. History of cervical surgery (LEEP, cone biopsy) raises suspicion.",
    impactOnFertility:
      "Cervical stenosis can impair natural sperm transport and make intrauterine procedures technically challenging. A difficult embryo transfer is associated with lower pregnancy rates due to catheter manipulation, bleeding, and potential uterine contractions. Identifying and addressing stenosis before the transfer cycle is key.",
    prevalence:
      "Uncommon in the general population. More frequent in women with prior cervical surgery — LEEP/cone biopsy increases the risk significantly.",
    treatmentOptions: [
      "Cervical dilation before embryo transfer cycle (under anesthesia or in office)",
      "Laminaria or osmotic dilators placed the day before transfer",
      "Use of a stylet or stiffer catheter during transfer",
      "Mock transfer to map the anatomy and plan the approach",
      "Hysteroscopy to evaluate and dilate under direct visualization",
      "In severe refractory cases, transmyometrial embryo transfer (rare)",
    ],
    prognosis:
      "With proper preparation (cervical dilation and mock transfer), most cases of cervical stenosis are manageable and do not significantly impact IVF outcomes. The key is identifying the issue before the actual transfer day to avoid a difficult or cancelled transfer.",
    forWhom: "her",
  },

  sources: [
    {
      title: "Performing the embryo transfer: a guideline",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2017,
      url: "https://pubmed.ncbi.nlm.nih.gov/28366416/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(cervicalStenosis);
